import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { soccerPath, soccerUrl } from '../utils/paths';
import { isPracticeSessionEnded } from '../utils/practiceSchedule';
import './PracticeScrimmageStats.css';

const sortByName = (a, b) =>
  (a?.playerName || '').localeCompare(b?.playerName || '', undefined, { sensitivity: 'base' });

const PracticeScrimmageStats = () => {
  const { id: practiceId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [practice, setPractice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showGoalForm, setShowGoalForm] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [goalForm, setGoalForm] = useState({
    minute: '',
    teamSide: 'RED',
    scorerPlayerId: '',
    assistPlayerId: '',
  });

  const loadStats = useCallback(async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      const [data, practiceData] = await Promise.all([
        api.getPracticeScrimmageStats(practiceId, user.id),
        api.getPracticeById(practiceId),
      ]);
      setStats(data);
      setPractice(practiceData);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load scrimmage stats');
    } finally {
      setLoading(false);
    }
  }, [practiceId, user?.id]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const redPlayers = useMemo(
    () => (stats?.playerStats || []).filter((p) => p.teamSide === 'RED').sort(sortByName),
    [stats]
  );
  const bluePlayers = useMemo(
    () => (stats?.playerStats || []).filter((p) => p.teamSide === 'BLUE').sort(sortByName),
    [stats]
  );
  const unassignedPlayers = useMemo(
    () => (stats?.playerStats || []).filter((p) => !p.teamSide).sort(sortByName),
    [stats]
  );

  const allPlayersForGoal = stats?.playerStats || [];

  const handleAddGoal = async (e) => {
    e.preventDefault();
    if (!goalForm.scorerPlayerId || goalForm.minute === '') {
      setError('Minute and scorer are required');
      return;
    }
    setActionLoading(true);
    setError('');
    try {
      const data = await api.addScrimmageGoal(practiceId, user.id, {
        minute: parseInt(goalForm.minute, 10),
        teamSide: goalForm.teamSide,
        scorerPlayerId: goalForm.scorerPlayerId,
        assistPlayerId: goalForm.assistPlayerId || null,
      });
      setStats(data);
      setGoalForm({ minute: '', teamSide: 'RED', scorerPlayerId: '', assistPlayerId: '' });
      setShowGoalForm(false);
      setSuccess('Goal recorded');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError(err.message || 'Failed to add goal');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveGoal = async (goalId) => {
    if (!window.confirm('Remove this goal?')) return;
    setActionLoading(true);
    setError('');
    try {
      const data = await api.removeScrimmageGoal(practiceId, goalId, user.id);
      setStats(data);
    } catch (err) {
      setError(err.message || 'Failed to remove goal');
    } finally {
      setActionLoading(false);
    }
  };

  const handleIncrementStat = async (playerId, statType) => {
    setActionLoading(true);
    setError('');
    try {
      const data = await api.incrementScrimmageStat(practiceId, user.id, playerId, statType);
      setStats(data);
    } catch (err) {
      setError(err.message || 'Failed to update stat');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDecrementStat = async (playerId, statType) => {
    setActionLoading(true);
    setError('');
    try {
      const data = await api.decrementScrimmageStat(practiceId, user.id, playerId, statType);
      setStats(data);
    } catch (err) {
      setError(err.message || 'Failed to undo stat');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReset = async () => {
    if (
      !window.confirm(
        'Reset this scrimmage? All goals, scores, and player stats will be cleared. The share link stays the same.'
      )
    ) {
      return;
    }
    setActionLoading(true);
    setError('');
    try {
      const data = await api.resetScrimmageStats(practiceId, user.id);
      setStats(data);
      setSuccess('Scrimmage reset — starting fresh');
      setTimeout(() => setSuccess(''), 2500);
    } catch (err) {
      setError(err.message || 'Failed to reset scrimmage');
    } finally {
      setActionLoading(false);
    }
  };

  const handleFinalize = async () => {
    const isRefinalize = stats?.status === 'FINAL';
    const message = isRefinalize
      ? 'Re-mark scrimmage as final? This updates player profile stats with your latest changes.'
      : 'Mark scrimmage as final? You can still edit stats later and re-mark as final.';
    if (!window.confirm(message)) return;
    setActionLoading(true);
    try {
      const data = await api.finalizeScrimmageStats(practiceId, user.id);
      setStats(data);
      setSuccess('Scrimmage marked as final');
      setTimeout(() => setSuccess(''), 2500);
    } catch (err) {
      setError(err.message || 'Failed to finalize');
    } finally {
      setActionLoading(false);
    }
  };

  const getShareToken = async () => {
    if (stats?.shareToken) return stats.shareToken;
    const share = await api.ensureScrimmageShare(practiceId, user.id);
    if (share?.shareToken) {
      setStats((prev) => (prev ? { ...prev, shareToken: share.shareToken } : prev));
      return share.shareToken;
    }
    return null;
  };

  const handleShareWhatsApp = async () => {
    try {
      const token = await getShareToken();
      if (!token) {
        setError('Could not create share link');
        return;
      }
      const url = api.shareScrimmageStatsViaWhatsApp({
        title: stats?.practiceTitle,
        practiceDate: stats?.practiceDate,
        location: stats?.location,
        shareToken: token,
        scoreRed: stats?.scoreRed,
        scoreBlue: stats?.scoreBlue,
        goals: stats?.goals,
      });
      window.open(url, '_blank');
    } catch (err) {
      setError(err.message || 'Failed to share');
    }
  };

  const handleCopyLink = async () => {
    try {
      const token = await getShareToken();
      if (!token) return;
      const link = soccerUrl(`/public/scrimmage-stats/${token}`);
      await navigator.clipboard.writeText(link);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2500);
    } catch (err) {
      setError(err.message || 'Failed to copy link');
    }
  };

  const renderStatControl = (player, statType, label, count) => (
    <div className="scrimmage-stat-control">
      <button
        type="button"
        className="btn btn-sm scrimmage-stat-btn scrimmage-stat-btn--minus"
        disabled={actionLoading || count <= 0}
        onClick={() => handleDecrementStat(player.playerId, statType)}
        aria-label={`Undo ${label} for ${player.playerName}`}
      >
        −
      </button>
      <span className="scrimmage-stat-control__label">
        {label}
        {count > 0 && <span className="scrimmage-stat-control__count">{count}</span>}
      </span>
      <button
        type="button"
        className="btn btn-sm scrimmage-stat-btn"
        disabled={actionLoading}
        onClick={() => handleIncrementStat(player.playerId, statType)}
        aria-label={`Add ${label} for ${player.playerName}`}
      >
        +
      </button>
    </div>
  );

  const renderPlayerRow = (player) => (
    <div key={player.playerId} className="scrimmage-player-row">
      <div className="scrimmage-player-row__info">
        <strong>{player.playerName}</strong>
        <span className="scrimmage-player-row__counts">
          {player.goals > 0 && <span>{player.goals}G </span>}
          {player.assists > 0 && <span>{player.assists}A </span>}
          {player.saves > 0 && <span>{player.saves}SV </span>}
          {player.defended > 0 && <span>{player.defended}DEF</span>}
          {player.goals + player.assists + player.saves + player.defended === 0 && (
            <span className="scrimmage-player-row__empty">—</span>
          )}
        </span>
      </div>
      <div className="scrimmage-player-row__actions">
        {renderStatControl(player, 'SAVE', 'Save', player.saves)}
        {renderStatControl(player, 'DEFENDED', 'Def', player.defended)}
        {renderStatControl(player, 'ASSIST', 'Ast', player.assists)}
      </div>
    </div>
  );

  if (loading) {
    return <div className="loading">Loading scrimmage stats…</div>;
  }

  const sessionEnded = isPracticeSessionEnded(practice);

  return (
    <div className="scrimmage-stats">
      <div className="scrimmage-stats__header">
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <div>
          <h1>{sessionEnded ? 'Scrimmage stats' : 'Live scrimmage stats'}</h1>
          {stats?.practiceTitle && <p className="scrimmage-stats__subtitle">{stats.practiceTitle}</p>}
        </div>
        {stats?.status === 'FINAL' && (
          <span className="scrimmage-stats__badge scrimmage-stats__badge--final">Final</span>
        )}
      </div>

      {sessionEnded && (
        <div className="alert alert-info scrimmage-stats__post-practice-note">
          Practice has ended. You can still update scores and player statistics here. Mark the scrimmage
          as final again after your changes so player profiles reflect the latest stats.
        </div>
      )}

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="scrimmage-scoreboard">
        <div className="scrimmage-scoreboard__team scrimmage-scoreboard__team--red">
          <span className="scrimmage-scoreboard__label">🔴 Red</span>
          <span className="scrimmage-scoreboard__score">{stats?.scoreRed ?? 0}</span>
        </div>
        <div className="scrimmage-scoreboard__divider">–</div>
        <div className="scrimmage-scoreboard__team scrimmage-scoreboard__team--blue">
          <span className="scrimmage-scoreboard__label">🔵 Blue</span>
          <span className="scrimmage-scoreboard__score">{stats?.scoreBlue ?? 0}</span>
        </div>
      </div>

      <div className="scrimmage-stats__actions-bar">
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setShowGoalForm(!showGoalForm)}
          disabled={actionLoading}
        >
          {showGoalForm ? 'Cancel goal' : '+ Record goal'}
        </button>
        <button type="button" className="btn btn-whatsapp" onClick={handleShareWhatsApp}>
          📱 Share via WhatsApp
        </button>
        <button type="button" className="btn btn-secondary" onClick={handleCopyLink}>
          {linkCopied ? 'Link copied!' : 'Copy link'}
        </button>
        <button type="button" className="btn btn-outline-primary" onClick={handleFinalize} disabled={actionLoading}>
          {stats?.status === 'FINAL' ? 'Re-mark as final' : 'Mark final'}
        </button>
        <Link
          to={soccerPath(`/practices/${practiceId}/summary?fromScrimmage=1`)}
          className="btn btn-primary"
          style={{ textDecoration: 'none', fontWeight: 600 }}
        >
          📋 Create team summary
        </Link>
        <button type="button" className="btn btn-danger" onClick={handleReset} disabled={actionLoading}>
          Reset scrimmage
        </button>
        {stats?.shareToken && (
          <Link
            to={soccerPath(`/public/scrimmage-stats/${stats.shareToken}`)}
            target="_blank"
            className="btn btn-secondary"
            style={{ textDecoration: 'none' }}
          >
            Preview public page
          </Link>
        )}
      </div>

      {showGoalForm && (
        <form className="scrimmage-goal-form card" onSubmit={handleAddGoal}>
          <h3>Record goal</h3>
          <div className="scrimmage-goal-form__grid">
            <label>
              Minute
              <input
                type="number"
                min="0"
                max="200"
                value={goalForm.minute}
                onChange={(e) => setGoalForm({ ...goalForm, minute: e.target.value })}
                required
              />
            </label>
            <label>
              Team
              <select
                value={goalForm.teamSide}
                onChange={(e) => setGoalForm({ ...goalForm, teamSide: e.target.value })}
              >
                <option value="RED">🔴 Red</option>
                <option value="BLUE">🔵 Blue</option>
              </select>
            </label>
            <label>
              Scorer
              <select
                value={goalForm.scorerPlayerId}
                onChange={(e) => setGoalForm({ ...goalForm, scorerPlayerId: e.target.value })}
                required
              >
                <option value="">Select player</option>
                {allPlayersForGoal.map((p) => (
                  <option key={p.playerId} value={p.playerId}>
                    {p.playerName}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Assist (optional)
              <select
                value={goalForm.assistPlayerId}
                onChange={(e) => setGoalForm({ ...goalForm, assistPlayerId: e.target.value })}
              >
                <option value="">None</option>
                {allPlayersForGoal.map((p) => (
                  <option key={p.playerId} value={p.playerId}>
                    {p.playerName}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <button type="submit" className="btn btn-primary" disabled={actionLoading}>
            Add goal
          </button>
        </form>
      )}

      <section className="scrimmage-section card">
        <h2>Goal timeline</h2>
        {!stats?.goals?.length ? (
          <p className="scrimmage-empty">No goals recorded yet.</p>
        ) : (
          <ul className="scrimmage-goals-list">
            {[...stats.goals]
              .sort((a, b) => (a.minute ?? 0) - (b.minute ?? 0))
              .map((g) => (
                <li key={g.id} className="scrimmage-goal-item">
                  <span className="scrimmage-goal-item__minute">{g.minute}&apos;</span>
                  <span className="scrimmage-goal-item__team">
                    {g.teamSide === 'RED' ? '🔴' : '🔵'}
                  </span>
                  <span className="scrimmage-goal-item__text">
                    <strong>{g.scorerPlayerName}</strong>
                    {g.assistPlayerName && (
                      <span className="scrimmage-goal-item__assist"> (assist: {g.assistPlayerName})</span>
                    )}
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-danger scrimmage-goal-item__remove"
                    onClick={() => handleRemoveGoal(g.id)}
                    disabled={actionLoading}
                  >
                    Remove
                  </button>
                </li>
              ))}
          </ul>
        )}
      </section>

      <div className="scrimmage-teams-grid">
        {redPlayers.length > 0 && (
          <section className="scrimmage-section card scrimmage-section--red">
            <h2>🔴 Red team stats</h2>
            <p className="scrimmage-section__hint">
              {sessionEnded
                ? 'Use − / + to adjust Save, Def, or Ast after the session'
                : 'Use − / + to adjust Save, Def, or Ast during the scrimmage'}
            </p>
            {redPlayers.map(renderPlayerRow)}
          </section>
        )}
        {bluePlayers.length > 0 && (
          <section className="scrimmage-section card scrimmage-section--blue">
            <h2>🔵 Blue team stats</h2>
            <p className="scrimmage-section__hint">
              {sessionEnded
                ? 'Use − / + to adjust Save, Def, or Ast after the session'
                : 'Use − / + to adjust Save, Def, or Ast during the scrimmage'}
            </p>
            {bluePlayers.map(renderPlayerRow)}
          </section>
        )}
        {unassignedPlayers.length > 0 && (
          <section className="scrimmage-section card">
            <h2>All players</h2>
            <p className="scrimmage-section__hint">Form teams first to group by Red/Blue</p>
            {unassignedPlayers.map(renderPlayerRow)}
          </section>
        )}
      </div>
    </div>
  );
};

export default PracticeScrimmageStats;
